import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TablePagination,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import UpdateIcon from '@mui/icons-material/Update';
import BlockForeverIcon from '@mui/icons-material/Block';
import moment from 'moment';
import { theme } from '../../../theme';
import { axiosClient } from '../../../services/axiosClient';

const BansList = () => {
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBan, setSelectedBan] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, ban: null });
  const [processing, setProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchBans();
  }, [page, pageSize]);

  const fetchBans = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get('/superadmin/bans/', {
        params: {
          page: page + 1,
          page_size: pageSize
        }
      });
      const data = response.data;
      if (data?.results) {
        setBans(data.results);
        setTotal(data.count);
      } else {
        setBans(data);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching bans:', err);
      setError('Erreur lors du chargement des bans');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, ban) => {
    setAnchorEl(event.currentTarget);
    setSelectedBan(ban);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBan(null);
  };

  const handleActionClick = (action) => {
    setActionDialog({ open: true, action, ban: selectedBan });
    handleMenuClose();
  };

  const handleConfirmAction = async () => {
    setProcessing(true);
    try {
      const { action, ban } = actionDialog;

      if (action === 'lift') {
        await axiosClient.post(`/superadmin/bans/${ban.id}/lift/`);
      } else if (action === 'extend') {
        // Prolonger de 7 jours supplémentaires
        const newEndDate = moment(ban.end_date).add(7, 'days').toISOString();
        await axiosClient.patch(`/superadmin/bans/${ban.id}/`, { end_date: newEndDate });
      } else if (action === 'permanent') {
        // Changer en ban permanent
        await axiosClient.patch(`/superadmin/bans/${ban.id}/`, { ban_type: 'permanent', end_date: null });
      }

      setActionDialog({ open: false, action: null, ban: null });
      fetchBans();
    } catch (err) {
      console.error('Error performing action:', err);
      setError(`Erreur lors de l'action: ${err.response?.data?.detail || err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const getBanTypeLabel = (type) => {
    const labels = {
      'warning': 'Avertissement',
      'temporary': 'Temporaire (1 semaine)',
      'permanent': 'Définitif'
    };
    return labels[type] || type;
  };

  const getBanTypeColor = (type) => {
    const colors = {
      'warning': 'warning',
      'temporary': 'error',
      'permanent': 'error'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pb: 12 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: theme.colors.text.primary }}>
        <BlockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Gestion des Bans
      </Typography>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.colors.primary[50] }}>
                <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type de Ban</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Absences</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date de début</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date de fin</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Raison</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Aucun ban enregistré</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bans.map((ban) => (
                  <TableRow key={ban.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ban.client_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {ban.client_email || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getBanTypeLabel(ban.ban_type)}
                        color={getBanTypeColor(ban.ban_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${ban.no_show_count} absence(s)`}
                        size="small"
                        sx={{ backgroundColor: theme.colors.error[100], color: theme.colors.error[700] }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {moment(ban.start_date).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ban.end_date ? moment(ban.end_date).format('DD/MM/YYYY HH:mm') : 'Permanent'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={ban.is_active ? <BlockIcon /> : <CheckCircleIcon />}
                        label={ban.is_active ? 'Actif' : 'Levé'}
                        color={ban.is_active ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {ban.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, ban)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: { xs: 0, md: '280px' },
          zIndex: 1000,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <Paper
            elevation={3}
            sx={{
              pointerEvents: 'auto',
              borderRadius: theme.borderRadius.xl,
              border: `1px solid ${theme.colors.border.light}`,
              overflow: 'hidden',
              width: 'fit-content'
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Lignes par page"
              sx={{ bgcolor: 'white' }}
            />
          </Paper>
        </Box>
      )}

      {/* Menu des actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedBan?.is_active && (
          <MenuItem onClick={() => handleActionClick('lift')}>
            <ListItemIcon>
              <CheckIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Lever le ban</ListItemText>
          </MenuItem>
        )}
        {selectedBan?.is_active && selectedBan?.ban_type === 'temporary' && (
          <MenuItem onClick={() => handleActionClick('extend')}>
            <ListItemIcon>
              <UpdateIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Prolonger de 7 jours</ListItemText>
          </MenuItem>
        )}
        {selectedBan?.is_active && selectedBan?.ban_type !== 'permanent' && (
          <MenuItem onClick={() => handleActionClick('permanent')}>
            <ListItemIcon>
              <BlockForeverIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Bannir définitivement</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Dialog de confirmation */}
      <Dialog
        open={actionDialog.open}
        onClose={() => !processing && setActionDialog({ open: false, action: null, ban: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {actionDialog.action === 'lift' && 'Lever le ban'}
          {actionDialog.action === 'extend' && 'Prolonger le ban'}
          {actionDialog.action === 'permanent' && 'Bannir définitivement'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.action === 'lift' && (
            <Typography>
              Êtes-vous sûr de vouloir lever le ban pour <strong>{actionDialog.ban?.client_name}</strong> ?
              Le client pourra à nouveau prendre des rendez-vous.
            </Typography>
          )}
          {actionDialog.action === 'extend' && (
            <Typography>
              Prolonger le ban de <strong>{actionDialog.ban?.client_name}</strong> de 7 jours supplémentaires ?
              Nouvelle date de fin : <strong>{moment(actionDialog.ban?.end_date).add(7, 'days').format('DD/MM/YYYY')}</strong>
            </Typography>
          )}
          {actionDialog.action === 'permanent' && (
            <>
              <Typography color="error" sx={{ fontWeight: 600 }}>
                ⚠️ ATTENTION : Action irréversible
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Bannir définitivement <strong>{actionDialog.ban?.client_name}</strong> ?
                Le client ne pourra plus jamais prendre de rendez-vous sur la plateforme.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setActionDialog({ open: false, action: null, ban: null })}
            disabled={processing}
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={actionDialog.action === 'lift' ? 'success' : actionDialog.action === 'extend' ? 'warning' : 'error'}
            disabled={processing}
          >
            {processing ? 'Traitement...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BansList;
